import {
  Map as MapBase,
  MapOverlay,
  MapProvider,
  MapboxProvider,
  ZoomInButton,
  ZoomOutButton,
  useMapContext,
  useMapLayerFilter,
  useMapViewport,
  useMapNavigation
} from '@ljagis/react-mapping';
import {
  Box,
  BoxProps,
  Checkbox,
  Divider,
  FormControlLabel,
  Hidden,
  makeStyles
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import SatelliteButton from '../../../../components/MapOverlay/SatelliteButton';
import LayerToggle from './LayerToggle';
import satelliteStreetsStyle from './styles/satellite.json';
import streetsStyle from './styles/streets.json';

const TEXAS_BOUNDS = [-106.645646, 25.837377, -93.508292, 36.500704];

// Hardcoded mapping to style layers used in component.
// These could be more dynamic (ex. defined in style via metadata) if needed,
// but doesn't provide much value with the current map styles. Same layers in all.
/** Map style layer to call `onDistrictsClick` when clicked. Should be fill layer for best UX. */
const DISTRICT_LAYER_ID_CLICK = 'awbd-districts';
/** Map style layers for primary display of districts. Will be filtered with `displayedDistrictIds`. */
const DISTRICT_LAYER_IDS_FILTERED = [
  'awbd-districts',
  'awbd-districts-border',
  'awbd-districts-marker',
  'awbd-districts-label'
];
/** Map style layers for display of selected district (`selectedDistrictId`) */
const DISTRICT_LAYER_IDS_SELECTED = [
  'awbd-districts-selected',
  'awbd-districts-selected-border',
  'awbd-districts-selected-marker',
  'awbd-districts-selected-label'
];
/** Attribute in district map data to use as ID. Should match `DistrictID` values in props. */
const DISTRICT_ID_ATTRIBUTE = 'DISTRICT_ID';

type DistrictId = string;

type xmin = number;
type ymin = number;
type xmax = number;
type ymax = number;

/** Component for displaying AWBD districts by ID and highlighting selected district */
export interface DistrictDisplayProps {
  /** IDs of districts to display on the map */
  displayedDistrictsIds?: DistrictId[];
  /**
   * ID of district to select.
   * Selected district will display with a filled boundary and highlighted label.
   * Will be still be displayed when not included in `displayedDistrictsIds`.
   */
  selectedDistrictId?: DistrictId;
  /**
   * Called when districts are clicked/tapped on the map by the user.
   * `districtIds` are the map ID values for visible districts at the click location.
   * Multiple, overlapping districts could be returned from a single click.
   */
  onDistrictsClick?: (districtIds: DistrictId[]) => void;
}

/** Component for managing map bounds for use in district filtering */
export interface FilterBoundsProps {
  /**
   * Map bounds expected for use when filtering have changed.
   * Bounds in format `[xmin, ymin, xmax, ymax]`.
   * Called with `bounds = null` when option to use bounds is toggled off by user.
   */
  onFilterBoundsChange?: (bounds: [xmin, ymin, xmax, ymax] | null) => void;
}

export interface NavigableMapProps {
  /** Trigger map navigation */
  navigation?: { extent: [xmin, ymin, xmax, ymax]; timestamp: number };
}

/** Presentation of root component */
export type RootComponentProps = Omit<BoxProps, 'children'>;

export type MapProps = DistrictDisplayProps &
  FilterBoundsProps &
  NavigableMapProps &
  RootComponentProps & { isFullPageWidth: boolean };

const useStyles = makeStyles((theme) => ({
  mapOverlay: {
    overflow: 'hidden',
    background: theme.palette.background.default
  },
  formLabelRoot: {
    margin: theme.spacing(0, 1.5, 0, 0)
  },
  formLabelLabel: {
    ...theme.typography.body2,
    whiteSpace: 'nowrap'
  }
}));

const Map: React.VFC<MapProps> = ({
  displayedDistrictsIds = [],
  selectedDistrictId,
  navigation,
  isFullPageWidth,
  onDistrictsClick,
  onFilterBoundsChange,
  ...rootProps
}) => {
  const classes = useStyles();

  const [isBoundsFilter, setIsBoundsFilter] = useState<boolean>(false);
  /** Can be used to detect style changes (ex. streets -> satellite) */
  const [mapStyleName, setMapStyleName] = useState<string>(streetsStyle.name);

  const { map: mapInstance } = useMapContext();
  const { bounds } = useMapViewport();
  const { applyFilter } = useMapLayerFilter();
  const { fitBounds } = useMapNavigation();

  // #region Update `setMapStyleName` on map style changes
  useEffect(() => {
    if (!mapInstance) return undefined;

    // `target` is a Mapbox map control. Using `any` to avoid importing Mapbox type definitions.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onMapStyleChange = ({ target }: any) => {
      const newMapStyle = target?.getStyle && target.getStyle();
      setMapStyleName(newMapStyle?.name || new Date().valueOf().toString());
    };

    mapInstance.on('style.load', onMapStyleChange);

    return () => {
      mapInstance.off('style.load', onMapStyleChange);
    };
  }, [mapInstance]);
  // #endregion

  // #region Call `onFilterBoundsChange` when map bounds change or toggle is unchecked
  useEffect(() => {
    if (!(isBoundsFilter && onFilterBoundsChange && bounds)) return;
    onFilterBoundsChange(
      bounds.toArray().flat() as [number, number, number, number]
    );
  }, [isBoundsFilter, onFilterBoundsChange, bounds]);

  useEffect(() => {
    // Need `useEffect` without `bounds` as a dependency to call with `null` only when unchecked
    if (isBoundsFilter || !onFilterBoundsChange) return;
    onFilterBoundsChange(null);
  }, [isBoundsFilter, onFilterBoundsChange]);
  // #endregion

  // #region Call `onDistrictsClick` when District features are clicked on the map
  // One layer (`DISTRICT_LAYER_ID_CLICK`) will be watched for click events.
  useEffect(() => {
    if (!(mapInstance && onDistrictsClick)) return undefined;

    const onLayerClick = ({ features }: { features?: GeoJSON.Feature[] }) => {
      const idLookup: Record<string, 1> = {};
      const uniqueFeatureIds = (features || []).reduce((acc, feature) => {
        const id =
          feature.properties && feature.properties[DISTRICT_ID_ATTRIBUTE];
        if (!id) return acc; // Feature has no district ID value or feature IDs are not configured (see promoteId in style)
        if (idLookup[id]) return acc; // Not unique, ID already in results
        idLookup[id] = 1; // Just needs to be truthy for lookup
        return [...acc, id.toString()];
      }, [] as string[]);

      uniqueFeatureIds.length && onDistrictsClick(uniqueFeatureIds);
    };

    const onLayerMouseEnter = () => {
      mapInstance.getCanvas().style.cursor = 'pointer';
    };

    const onLayerMouseLeave = () => {
      mapInstance.getCanvas().style.cursor = '';
    };

    const districtLayerId = DISTRICT_LAYER_ID_CLICK;

    mapInstance.on('click', districtLayerId, onLayerClick);
    mapInstance.on('mouseenter', districtLayerId, onLayerMouseEnter);
    mapInstance.on('mouseleave', districtLayerId, onLayerMouseLeave);

    return () => {
      mapInstance.off('click', districtLayerId, onLayerClick);
      mapInstance.off('mouseenter', districtLayerId, onLayerMouseEnter);
      mapInstance.off('mouseleave', districtLayerId, onLayerMouseLeave);
    };
  }, [mapInstance, onDistrictsClick]);
  // #endregion

  // #region Display only district matching `displayedDistrictIds`
  // Layers to filter defined by `DISTRICT_LAYER_IDS_FILTERED`.
  // Filtered districts and selected districts are independent. A selected district that is
  // not included in `displayedDistrictIds` will still be displayed with the selected symbol.
  useEffect(() => {
    // Only using `mapStyleName` to trigger on map style change

    if (!applyFilter) return;

    const layerIdsToFilter = DISTRICT_LAYER_IDS_FILTERED;
    const idAttribute = DISTRICT_ID_ATTRIBUTE;

    const displayedDistrictIdsFilter = [
      'in',
      ['get', idAttribute],
      ['literal', displayedDistrictsIds]
    ];
    applyFilter(layerIdsToFilter, displayedDistrictIdsFilter);
  }, [displayedDistrictsIds, applyFilter, mapStyleName]);
  // #endregion

  // #region Highlight selected district
  // Map should include a layer (or layers) just for displaying
  // the selected district (`DISTRICT_LAYER_IDS_SELECTED`).
  // If selected districts have their own layer, the layer can be better placed
  // in the style layer order (ex. in front of filtered districts layer),
  // and we will have much more control over the symbology of selected features.
  useEffect(() => {
    // Only using `mapStyleName` to trigger on map style change

    if (!(selectedDistrictId && applyFilter)) return undefined;

    const layerIdsToFilter = DISTRICT_LAYER_IDS_SELECTED;
    const idAttribute = DISTRICT_ID_ATTRIBUTE;

    const selectedDistrictIdFilter = ['==', idAttribute, selectedDistrictId];
    applyFilter(layerIdsToFilter, selectedDistrictIdFilter);

    return () => {
      const filterWithNoMatches = ['==', idAttribute, 'NOMATCH'];
      applyFilter(layerIdsToFilter, filterWithNoMatches);
    };
  }, [selectedDistrictId, applyFilter, mapStyleName]);
  // #endregion

  // #region Navigate map on `navigation` changes
  useEffect(() => {
    if (!navigation?.extent) return;
    fitBounds(navigation.extent, { padding: 80, maxZoom: 16 });
  }, [navigation, fitBounds]);
  // #endregion

  return (
    <Box position="relative" style={{ overflow: 'hidden' }} {...rootProps}>
      <MapBase
        style={{ height: '100%', width: '100%' }}
        minZoom={3}
        maxZoom={15}
        initialMapStyle={streetsStyle as never} // Actually supports style JSON, but typing is only string
        initialBounds={TEXAS_BOUNDS as [number, number, number, number]}
        initialBoundsPadding={24}
      />

      <Hidden smDown={!isFullPageWidth} implementation="css">
        <Box
          position="absolute"
          top={0}
          left={0}
          m={0.5}
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Box m={0.5}>
            <MapOverlay className={classes.mapOverlay}>
              <FormControlLabel
                classes={{
                  root: classes.formLabelRoot,
                  label: classes.formLabelLabel
                }}
                labelPlacement="end"
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    name="isBoundsFilter"
                    checked={isBoundsFilter}
                    onChange={(evt) => setIsBoundsFilter(evt.target.checked)}
                  />
                }
                label="Limit search to map area"
              />
            </MapOverlay>
          </Box>
        </Box>

        <Box
          position="absolute"
          top={0}
          bottom={25} // Space for attribution control
          right={0}
          m={0.5}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Box m={0.5}>
            <MapOverlay className={classes.mapOverlay}>
              <ZoomInButton />
              <Divider />
              <ZoomOutButton />
            </MapOverlay>
          </Box>
          <Box m={0.5}>
            <MapOverlay className={classes.mapOverlay}>
              <SatelliteButton
                streetsStyle={streetsStyle as never}
                satelliteStyle={satelliteStreetsStyle as never}
              />
            </MapOverlay>
          </Box>
          <Box m={0.5}>
            <MapOverlay className={classes.mapOverlay}>
              <LayerToggle />
            </MapOverlay>
          </Box>
        </Box>
      </Hidden>
    </Box>
  );
};

// #region Map context wrap
type MapContextProps = { mapboxAccessToken: string };

const MapWithContext: React.VFC<MapContextProps & MapProps> = ({
  mapboxAccessToken,
  ...props
}) => (
  <MapboxProvider accessToken={mapboxAccessToken}>
    <MapProvider>
      <Map {...props} />
    </MapProvider>
  </MapboxProvider>
);
// #endregion

export default MapWithContext;
